'use client';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { Locale, LOCALE_NAMES } from '@ui/lib/locale';
import { Globe } from 'lucide-react';
import { useRef } from 'react';
import { cn } from '@ui/lib/utils';

interface LanguageSelectorProps {
  onValueChange(locale: Locale): void;
  value: Locale | null;
  className?: string;
  variant?: 'default' | 'icon' | 'full';
}

export default function LanguageSelector({ onValueChange, value, className = '', variant = 'default' }: LanguageSelectorProps) {
  const isUserInteraction = useRef(false);
  const iconOnly = variant === 'icon';

  function onSelectChange(nextLocale: Locale) {
    if (isUserInteraction.current) {
      onValueChange(nextLocale);
    }
  }

  const handleOpenChange = () => {
    isUserInteraction.current = true;
  };

  return (
    <Select value={value || ''} onValueChange={onSelectChange} onOpenChange={handleOpenChange}>
      <SelectTrigger
        className={cn('w-full min-w-32 max-w-48', className, iconOnly ? 'border-0 shadow-none p-2 min-w-0' : '')}
        aria-label="Language switcher"
        iconOnly={iconOnly}
      >
        {variant !== 'default' && <Globe className="w-4 h-4" />}
        {!iconOnly && <SelectValue />}
      </SelectTrigger>
      <SelectContent>
        {Object.entries(LOCALE_NAMES)
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([code, name]) => (
            <SelectItem key={code} value={code}>
              {name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
